import React, { useCallback, useState } from "react";
import { Table, Button, Input, Alert, Tooltip } from "antd";
import NumberInput from "../common/NumberInput";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { DeleteOutlined } from "@ant-design/icons";
import { CarriagewayFootway } from "../../types/tender.types";
import { COLORS } from "../../utils/constants";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";

interface FootwaysTakeoffSectionProps {
  carriagewayFootways: CarriagewayFootway[];
  onUpdate: (carriagewayFootways: CarriagewayFootway[]) => void;
  // Row selection is controlled by the parent so its tab-level "Delete
  // Selected" button can act on it.
  selectedRowKeys: React.Key[];
  onSelectedRowKeysChange: (keys: React.Key[]) => void;
  stickyOffset?: number;
  isViewMode?: boolean;
}

const PATH_AREA_FIELDS: Array<keyof CarriagewayFootway> = [
  "tarmacPath", "tarmacCrossover", "heavyDutyCrossover",
  "blockPavedPathCrossover", "cycleway", "verge",
];

const ColTitle: React.FC<{ title: string }> = ({ title }) => {
  const unitMatch = title.match(/\s+(m[²³]?|Nr)$/);
  if (!unitMatch) {
    return (
      <Tooltip title={title}>
        <div className="ftw-header-title">{title}</div>
      </Tooltip>
    );
  }
  const unit = unitMatch[1];
  const name = title.replace(/\s+(m[²³]?|Nr)$/, "");
  return (
    <div className="text-center">
      <Tooltip title={name}>
        <div className="ftw-header-title">{name}</div>
      </Tooltip>
      <div className="ftw-header-unit">{unit}</div>
    </div>
  );
};

const FootwaysTakeoffSection: React.FC<FootwaysTakeoffSectionProps> = ({
  carriagewayFootways,
  onUpdate,
  selectedRowKeys,
  onSelectedRowKeysChange,
  stickyOffset = 310,
  isViewMode = false,
}) => {
  // Defers this table's first mount by one paint cycle so a tender with a
  // lot of roads doesn't make this tab look frozen while the table's
  // initial render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Purely a perceived-lag fix:
  // once it flips false the table renders exactly as before.
  const isTableLoading = useTableRenderDeferral();

  const deleteRecords = useCallback(
    (ids: React.Key[]) => {
      const idSet = new Set(ids);
      onUpdate(carriagewayFootways.filter((item) => !idSet.has(item.id)));
    },
    [carriagewayFootways, onUpdate],
  );

  // Delete confirmation modal — matches the DeleteConfirmationModal pattern used
  // by Groundworks tables, rather than the antd Popconfirm previously used here.
  // idsToDelete is used for single-row delete; bulk delete is triggered by the
  // parent (its "Delete Selected" button lives at the tab level).
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState<React.Key[]>([]);
  const confirmDelete = () => {
    deleteRecords(idsToDelete);
    setDeleteModalVisible(false);
    setIdsToDelete([]);
    onSelectedRowKeysChange([]);
  };
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setIdsToDelete([]);
  };

  const updateField = useCallback(
    (id: string, field: keyof CarriagewayFootway, value: string | number) => {
      const updated = carriagewayFootways.map((item) => {
        if (item.id !== id) return item;
        const next = { ...item, [field]: value };
        if (PATH_AREA_FIELDS.includes(field)) {
          next.totalPath =
            (next.tarmacPath || 0) + (next.tarmacCrossover || 0) +
            (next.heavyDutyCrossover || 0) + (next.blockPavedPathCrossover || 0) +
            (next.cycleway || 0) + (next.verge || 0);
        }
        return next;
      });
      onUpdate(updated);
    },
    [carriagewayFootways, onUpdate],
  );

  const totals = carriagewayFootways.reduce(
    (acc, item) => ({
      tarmacPath: acc.tarmacPath + (item.tarmacPath || 0),
      tarmacCrossover: acc.tarmacCrossover + (item.tarmacCrossover || 0),
      heavyDutyCrossover: acc.heavyDutyCrossover + (item.heavyDutyCrossover || 0),
      blockPavedPathCrossover: acc.blockPavedPathCrossover + (item.blockPavedPathCrossover || 0),
      cycleway: acc.cycleway + (item.cycleway || 0),
      verge: acc.verge + (item.verge || 0),
      totalPath: acc.totalPath + (item.totalPath || 0),
    }),
    {
      tarmacPath: 0, tarmacCrossover: 0, heavyDutyCrossover: 0,
      blockPavedPathCrossover: 0, cycleway: 0, verge: 0, totalPath: 0,
    },
  );

  const inputCol = (
    title: string,
    dataIndex: keyof CarriagewayFootway,
    width: number,
    precision = 3,
  ) => ({
    title: <ColTitle title={title} />,
    dataIndex,
    key: dataIndex as string,
    width,
    align: "right" as const,
    render: (value: number, record: CarriagewayFootway) => (
      <NumberInput
        value={value ?? 0}
        onChange={(val) => updateField(record.id, dataIndex, val)}
        decimalPlaces={precision}
        isInteger={precision === 0}
        style={{ width: "100%", textAlign: "right" }}
        size="small"
      />
    ),
  });

  const calcCol = (title: string, dataIndex: keyof CarriagewayFootway, width: number) => ({
    title: <ColTitle title={title} />,
    dataIndex,
    key: dataIndex as string,
    width,
    align: "right" as const,
    render: (value: number) => (
      <span style={{ fontWeight: 600, color: COLORS.primary }}>
        {(value || 0).toFixed(3)}
      </span>
    ),
  });

  const columns = [
    {
      title: "Road Name",
      dataIndex: "roadName",
      key: "roadName",
      width: 140,
      fixed: "left" as const,
      render: (value: string, record: CarriagewayFootway) => (
        <Input
          value={value}
          onChange={(e) => updateField(record.id, "roadName", e.target.value)}
          placeholder="Road name"
          size="small"
        />
      ),
    },
    inputCol("Tarmac Path m²", "tarmacPath", 120),
    inputCol("Tarmac Crossover m²", "tarmacCrossover", 145),
    inputCol("Heavy Duty Crossover m²", "heavyDutyCrossover", 165),
    inputCol("Block Paved Path / Crossover m²", "blockPavedPathCrossover", 205),
    inputCol("Cycleway m²", "cycleway", 110),
    inputCol("Verge m²", "verge", 100),
    calcCol("Path Total m²", "totalPath", 115),
    {
      title: "Actions",
      key: "actions",
      width: 75,
      fixed: "right" as const,
      align: 'center' as const,
      render: (_: unknown, record: CarriagewayFootway) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => {
            setIdsToDelete([record.id]);
            setDeleteModalVisible(true);
          }}
        />
      ),
    },
  ];

  const summaryLeaves = [
    "roadName",
    "tarmacPath", "tarmacCrossover", "heavyDutyCrossover",
    "blockPavedPathCrossover", "cycleway", "verge", "totalPath",
    "actions",
  ] as const;

  type TotalsKey = keyof typeof totals;

  return (
    <>
      {isTableLoading ? (
        <TableLoadingPlaceholder />
      ) : (
      <Table
        className="footways-takeoff-table"
        columns={columns}
        dataSource={carriagewayFootways}
        rowKey="id"
        rowSelection={isViewMode ? undefined : {
          selectedRowKeys,
          onChange: onSelectedRowKeysChange,
        }}
        pagination={false}
        scroll={{ x: 1200 }}
        sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
        size="small"
        bordered
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}>
              {/* colSpan merges the row-selection checkbox column (when present) with Road Name,
                  since rowSelection inserts a checkbox column before all defined columns. */}
              <Table.Summary.Cell key="roadName" index={0} colSpan={isViewMode ? 1 : 2} align="center">
                <span style={{ color: "#000" }}>Totals</span>
              </Table.Summary.Cell>
              {summaryLeaves.filter((key) => key !== "roadName").map((key, idx) => {
                // Accounts for the checkbox column (when present) and the merged Totals cell above.
                const colIndex = idx + (isViewMode ? 1 : 2);
                if (key === "actions") {
                  return <Table.Summary.Cell key="actions" index={colIndex} />;
                }
                const val = (totals[key as TotalsKey] as number) || 0;
                return (
                  <Table.Summary.Cell key={key} index={colIndex} align="right">
                    <span
                      style={{
                        color: key === "totalPath" ? COLORS.primary : "#000",
                        paddingRight: summaryLeaves.length-1 ==colIndex?0:7.5,
                      }}
                    >
                      {val.toFixed(3)}
                    </span>
                  </Table.Summary.Cell>
                  
                );
              })}
              
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      )}

      {carriagewayFootways.length === 0 && (
        <Alert
          message="No footways records. Click 'Add Road' to get started."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      <style>{`
        .footways-takeoff-table {
          font-size: 14px;
        }

        .footways-takeoff-table .ant-table {
          border: none !important;
        }

        .footways-takeoff-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-radius: 0 !important;
        }

        .footways-takeoff-table .ant-table-thead > tr > th {
          min-width: 100px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 10px 6px 8px 6px !important;
          text-align: center !important;
          font-size: 12px !important;
          vertical-align: top !important;
        }

        .footways-takeoff-table .ant-table-thead > tr > th .ftw-header-title {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          display: block !important;
          width: 100% !important;
        }

        .footways-takeoff-table .ant-table-thead > tr > th .ftw-header-unit {
          font-size: 11px !important;
          color: #6b7280 !important;
          font-weight: 500 !important;
        }

        .footways-takeoff-table .ant-table-thead > tr > th.ant-table-cell-fix-left,
        .footways-takeoff-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          background-color: ${COLORS.tableHeaderBg} !important;
          z-index: 100 !important;
        }

        .footways-takeoff-table .ant-table-tbody > tr > td.ant-table-cell-fix-left {
          background-color: #fff !important;
        }

        .footways-takeoff-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
          background-color: #fafafa !important;
        }

        /* Bulk-select checkboxes should not highlight the row */
        .footways-takeoff-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }
        .footways-takeoff-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        /* The merged Totals cell (checkbox + Road Name) is fixed-left, so the
           tbody fix-left override above would otherwise paint it white instead
           of matching the rest of the totals row. */
        .footways-takeoff-table .ant-table-summary td.ant-table-cell-fix-left {
          background-color: #FFF7E6 !important;
        }
      `}</style>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={idsToDelete.length > 1 ? `Are you sure you want to delete ${idsToDelete.length} records?` : undefined}
      />
    </>
  );
};

export default FootwaysTakeoffSection;
